/**
 * Utilities for the Billing Dashboard
 */
import React from 'react'
import CoreEnums from '../../../../../core/utils/enums'
import coreReducer from '../../../../../core/utils/reducer'
import store from '../../../../../store'
import SystemUsersWrapper from '../../../../Admin/system-users/components/SystemUsersWrapper'
import AddPatientForm from '../../../../Admin/system-users/components/add/AddPatientForm'
import { InvoicesList, MedicalAidPaymentList, Reports, InvoiceForm } from '../../../components'

/**
 * Navigate to a specific tab in the application
 * @param {string} key - The key of the tab to navigate to
 */
export const handleDashboardCardClick = (key) => {
  const tabContent = {
    key: null,
    closable: true,
    label: null,
    children: null
  }

  switch (key) {
    case CoreEnums.tabKeys.INVOICES:
      tabContent.key = CoreEnums.tabKeys.INVOICES
      tabContent.label = CoreEnums.tabTitles.INVOICES
      tabContent.children = <InvoicesList />
      break
    case CoreEnums.tabKeys.PATIENTS:
      tabContent.key = CoreEnums.tabKeys.PATIENTS
      tabContent.label = CoreEnums.tabTitles.PATIENTS
      tabContent.children = <SystemUsersWrapper viewType='Patient' type='patient' />
      break
    case CoreEnums.tabKeys.REMITTANCES:
      tabContent.key = CoreEnums.tabKeys.REMITTANCES
      tabContent.label = CoreEnums.tabTitles.REMITTANCES
      tabContent.children = <MedicalAidPaymentList />
      break
    case CoreEnums.tabKeys.BILLING_REPORTS:
      tabContent.key = CoreEnums.tabKeys.BILLING_REPORTS
      tabContent.label = CoreEnums.tabTitles.BILLING_REPORTS
      tabContent.children = <Reports />
      break
    case CoreEnums.tabKeys.DASHBOARD_BILLING:
      tabContent.key = CoreEnums.tabKeys.DASHBOARD_BILLING
      tabContent.label = CoreEnums.tabTitles.DASHBOARD_BILLING
      // This would create a circular dependency, so we'll navigate to the tab without rendering the component
      tabContent.children = null
      break
    default:
      break
  }

  if (key) {
    store.dispatch(coreReducer.actions.addTab(tabContent))
  }
}

/**
 * Opens the new invoice form directly
 * This bypasses the invoices list and opens the form in a new tab
 */
export const openNewInvoiceForm = () => {
  const tabContent = {
    key: `${CoreEnums.tabKeys.INVOICES}_new`,
    closable: true,
    label: 'New Invoice',
    children: <InvoiceForm data={{}} isNew={true} handleGetInvoices={() => {}} />
  }

  store.dispatch(coreReducer.actions.addTab(tabContent))
}

/**
 * Opens the add patient form directly
 * This bypasses the patient list and opens the form in a new tab
 */
export const openAddPatientForm = () => {
  const tabKey = `${CoreEnums.tabKeys.PATIENTS}_new_patient_user`

  // Create a reset function that closes the tab
  const handleResetFunction = () => {
    store.dispatch(
      coreReducer.actions.closeTab({
        targetKey: tabKey,
        removeBreadcrumb: true
      })
    )
  }

  const tabContent = {
    key: tabKey,
    closable: true,
    label: 'Add New Patient',
    children: (
      <AddPatientForm
        resetFunction={handleResetFunction}
        shouldStayOnPage={true}
        refreshView={() => {
          // No refresh needed when used from dashboard
        }}
      />
    )
  }

  store.dispatch(coreReducer.actions.addTab(tabContent))
}

/**
 * Navigate to the modern billing dashboard
 */
export const navigateToModernDashboard = () => {
  handleDashboardCardClick(CoreEnums.tabKeys.DASHBOARD_BILLING)
}

/**
 * Returns the appropriate status color for a given status
 * @param {string} status - The status (paid, pending, overdue)
 * @returns {string} - The color code for the status
 */
export const getStatusColor = (status) => {
  switch (status) {
    case 'completed':
      return 'success'
    case 'pending':
      return 'warning'
    case 'draft':
      return 'geekblue'
    default:
      return 'warning'
  }
}

/**
 * Format a number as currency
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (value, decimals = 2) => {
  return `R ${value.toFixed(decimals)}`
}

/**
 * Calculate percentage change between two values
 * @param {number} current - Current value
 * @param {number} previous - Previous value
 * @returns {number} - Percentage change
 */
export const calculatePercentageChange = (current, previous) => {
  if (previous === 0) return 0
  return ((current - previous) / previous) * 100
}

/**
 * Format a date string
 * @param {string} dateString - Date string
 * @returns {string} - Formatted date
 */
export const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' })
}
